import classNames from 'classnames';
import React, {useEffect, useState} from 'react';
import {features} from 'pageflow/frontend';
import {
  ThirdPartyOptIn,
  ThirdPartyOptOutInfo,
  useDarkBackground,
  usePortraitOrientation,
  useContentElementEditorState,
  useI18n,
  useEntryMetadata
} from 'pageflow-scrolled/frontend';

import styles from './EarRealityPlayer.module.css';

export function EarRealityPlayer({configuration, contentElementId}) {
  const darkBackground = useDarkBackground();
  const entryMetadata = useEntryMetadata();

  const mainColor = configuration.mainColor ||
                    (darkBackground ? '#fff' : '#000');
  const secondaryColor = configuration.secondaryColor ||
                         (darkBackground ? '#ddd' : '#444');

  const key = [
    configuration.token,
    configuration.gameId,
    configuration.height,
    mainColor,
    secondaryColor,
    configuration.displayImages
  ].join('-');

  const props = {
    key: key,
    height: configuration.height,
    mainColor: mainColor,
    secondaryColor: secondaryColor,
    displayImages: configuration.displayImages,
    token: configuration.token,
    gameId: configuration.gameId,
    contentElementId: contentElementId,
    resetLabel: configuration.resetLabel,
    earRealityPlayerUrl: entryMetadata?.configuration?.earRealityPlayerUrl
  }

  function renderPlayer() {
    if (configuration.portraitHeight) {
      return (
        <OrientationAwarePlayer {...props}
                                portraitHeight={configuration.portraitHeight} />
      );
    }
    else {
      return (
        <Player {...props} />
      );
    }
  }

  return (
    <OptIn position={configuration.position}>
      {renderPlayer()}
    </OptIn>
  );
}

function OptIn({children, position}) {
  const [enabled, setEnabled] = useState('unknown');
  const [optOutInfoEnabled, setOptOutInfoEnabled] = useState(false);

  // features is currently not safe to use in SSR.
  useEffect(() => {
    setEnabled(features.isEnabled('ear_reality_opt_in'));
    setOptOutInfoEnabled(features.isEnabled('ear_reality_opt_out_info'));
  }, []);

  if (enabled === 'unknown') {
    return null;
  }

  if (!enabled) {
    return children;
  }

  return (
    <>
      <ThirdPartyOptIn providerName="earReality">
        {children}
      </ThirdPartyOptIn>
      {optOutInfoEnabled &&
       <ThirdPartyOptOutInfo providerName="earReality"
                             contentElementPosition={position} />}
    </>
  );
}

function OrientationAwarePlayer(props) {
  const height = usePortraitOrientation() ? props.portraitHeight : props.height;

  return (
    <Player key={height} {...props} height={height} />
  );
}

let scriptPromise;

function scriptLoaded(playerUrl) {
  scriptPromise = scriptPromise || new Promise(resolve => {
    const script = document.createElement('script');

    script.setAttribute('src', playerUrl || 'https://twist-tool.com/integration.js');
    script.setAttribute('data-token', '4vPH9ifP0sUIV74slXvcjrNGe0Lpto');
    script.setAttribute('data-async', 'true');

    script.addEventListener('load', resolve);

    document.head.appendChild(script);
  });

  return scriptPromise;
}

function Player({
  height,
  mainColor, secondaryColor,
  displayImages,
  token, gameId,
  contentElementId,
  resetLabel,
  earRealityPlayerUrl
}) {
  const {isEditable} = useContentElementEditorState();

  const {t} = useI18n();
  const id = `audiobook-player-${contentElementId}`;

  useEffect(() => {
    scriptLoaded(earRealityPlayerUrl).then(() => {
      window.$audioPlayer.init([id]);
    });
  }, [id, earRealityPlayerUrl]);

  function reset() {
    if (isEditable) {
      alert('Diese Funktion steht im Editor nicht zur Verfügung.');
      return;
    }

    window.$audioPlayer[id].reset()
  }

  return (
    <>
      <div id={id}
           className={classNames(styles.player, styles[`player-${height}`])}
           data-ear-reality-player
           data-game-id={gameId}
           data-spectrum-height={height}
           data-main-color={mainColor}
           data-secondary-color={secondaryColor}
           data-show-image={displayImages ? 'true' : 'false'}
           data-image-position="top">
      </div>
      <div className={styles.commands}>
        <button className={styles.reset}
                onClick={reset}>
          {resetLabel || t('pageflow_scrolled.public.ear_reality.reset')}
        </button>
      </div>
    </>
  );
}
